import { useCallback, useEffect } from 'react';
import { useSwipe } from './useSwipe';
import {
  SECTION_LABELS,
  TOTAL_SECTIONS,
  SWIPE_THRESHOLD,
} from '../components/attendee-mobile/constants';

/**
 * Result interface for the navigation controller hook.
 * Provides section navigation state and actions for the
 * Attendee mobile swipeable header panel system.
 */
export interface NavigationControllerResult {
  /** Current active section index (0–8) */
  currentIndex: number;
  /** Navigate directly to a specific section index (clamped to [0, 8]) */
  goTo: (index: number) => void;
  /** Navigate to the next section (no-op at index 8) */
  goNext: () => void;
  /** Navigate to the previous section (no-op at index 0) */
  goPrev: () => void;
  /** Ref to attach to the swipeable container element */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Human-readable name of the current section */
  sectionName: string;
  /** True when at the first section (My Weekend, index 0) */
  isFirst: boolean;
  /** True when at the last section (Settings, index 8) */
  isLast: boolean;
}

/**
 * Hook that manages swipeable navigation between the 9 fixed
 * Attendee mobile menu sections. Wraps the existing useSwipe hook
 * with section-specific clamping, labels, and boundary detection.
 *
 * Attach `containerRef` to the swipeable container element to enable
 * touch gesture detection with a 50px horizontal threshold.
 *
 * @returns NavigationControllerResult
 */
export function useNavigationController(): NavigationControllerResult {
  const { containerRef, currentIndex, goTo: swipeGoTo, goNext, goPrev } = useSwipe({
    threshold: SWIPE_THRESHOLD,
    totalItems: TOTAL_SECTIONS,
  });

  // Wrap goTo with explicit clamping for external callers
  const goTo = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, TOTAL_SECTIONS - 1));
      swipeGoTo(clamped);
    },
    [swipeGoTo],
  );

  const sectionName = SECTION_LABELS[currentIndex] ?? '';
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === TOTAL_SECTIONS - 1; // index === 8

  // Listen for programmatic navigation requests (e.g., from "View All" buttons)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail.index === 'number') {
        goTo(detail.index);
      }
    };
    window.addEventListener('navigate-to-panel', handler);
    return () => window.removeEventListener('navigate-to-panel', handler);
  }, [goTo]);

  return {
    currentIndex,
    goTo,
    goNext,
    goPrev,
    containerRef,
    sectionName,
    isFirst,
    isLast,
  };
}
